import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Redirect } from 'react-router-dom';
import {
  Button,
  CssBaseline,
  TextField,
  Grid,
  Box,
  Typography,
  Container,
  Alert,
  Paper
} from '@mui/material';
import { auth } from "../actions";

const Register = () => {
  const dispatch = useDispatch();
  const authState = useSelector(state => state.auth);

  const { isAuthenticated } = authState;

  // State
  const [step, setStep] = useState(1);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [authCode, setAuthCode] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [localError, setLocalError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Step 1: Send SMS Code
  const onSendCode = (e) => {
    e.preventDefault();
    setLocalError(null);
    setSuccessMsg(null);

    dispatch(auth.sendCode(phoneNumber))
      .then((data) => {
        setSuccessMsg("認証コードを送信しました。");
        setStep(2);
      })
      .catch((err) => {
        setLocalError(err.error || "予期せぬエラーが発生しました");
      });
  };

  // Step 2: Verify Code
  const onVerifyCode = (e) => {
    e.preventDefault();
    setLocalError(null);
    setSuccessMsg(null);

    dispatch(auth.verifyCode(phoneNumber, authCode))
      .then(() => {
        setSuccessMsg("電話番号の認証に成功しました。");
        setStep(3);
      })
      .catch((err) => {
        setLocalError(err.error || "認証コードが無効です");
      });
  };

  // Step 3: Register Details
  const onRegisterCreate = (e) => {
    e.preventDefault();
    setLocalError(null);

    if (password !== confirmPassword) {
      setLocalError("パスワードが一致しません。");
      return;
    }

    dispatch(auth.registerCreate(phoneNumber, email, password, firstName, lastName))
      .then(() => {
        // Force reload to ensure fresh state and fix "no practice data" display issue
        window.location.href = "/";
      })
      .catch((err) => {
        setLocalError(err.error || "登録に失敗しました");
      });
  };

  if (isAuthenticated) {
    return <Redirect to="/" />;
  }

  // Step 1: Phone Number Input
  const renderStep1 = () => (
    <Box component="form" onSubmit={onSendCode} noValidate sx={{ mt: 1, width: '100%' }}>
      <Typography variant="body1" sx={{ mb: 2 }}>
        アカウントを作成するには、電話番号を入力してください。SMSで認証コードを送信します。
      </Typography>
      <TextField
        margin="normal"
        required
        fullWidth
        id="phone"
        label="電話番号 (例: +819012345678)"
        name="phone"
        autoComplete="tel"
        autoFocus
        value={phoneNumber}
        onChange={(e) => setPhoneNumber(e.target.value)}
      />
      <Button
        type="submit"
        fullWidth
        variant="contained"
        sx={{ mt: 3, mb: 2 }}
      >
        認証コードを送信
      </Button>
    </Box>
  );

  // Step 2: Auth Code Input
  const renderStep2 = () => (
    <Box component="form" onSubmit={onVerifyCode} noValidate sx={{ mt: 1, width: '100%' }}>
      <Typography variant="body1" sx={{ mb: 2 }}>
        {phoneNumber} に送信された認証コードを入力してください。
      </Typography>
      <TextField
        margin="normal"
        required
        fullWidth
        id="code"
        label="認証コード (6桁)"
        name="code"
        value={authCode}
        onChange={(e) => setAuthCode(e.target.value)}
      />
      <Button
        type="submit"
        fullWidth
        variant="contained"
        sx={{ mt: 3, mb: 2 }}
      >
        認証する
      </Button>
      <Button
        fullWidth
        variant="text"
        onClick={() => setStep(1)}
      >
        電話番号を変更する
      </Button>
    </Box>
  );

  // Step 3: Details Input
  const renderStep3 = () => (
    <Box component="form" onSubmit={onRegisterCreate} noValidate sx={{ mt: 1, width: '100%' }}>
      <Alert severity="success" sx={{ mb: 2 }}>
        電話番号が認証されました。詳細を入力して登録を完了してください。
      </Alert>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            id="lastName"
            label="姓(Last Name)"
            name="lastName"
            autoComplete="family-name"
            autoFocus
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            autoComplete="given-name"
            name="firstName"
            required
            fullWidth
            id="firstName"
            label="名(First Name)"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </Grid>
      </Grid>
      <TextField
        margin="normal"
        required
        fullWidth
        id="email"
        label="メールアドレス"
        name="email"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <TextField
        margin="normal"
        required
        fullWidth
        name="password"
        label="パスワード"
        type="password"
        id="password"
        autoComplete="new-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <TextField
        margin="normal"
        required
        fullWidth
        name="confirmPassword"
        label="パスワード（確認用）"
        type="password"
        id="confirmPassword"
        autoComplete="new-password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        sx={{ mt: 3, mb: 2 }}
      >
        登録を完了する
      </Button>
    </Box>
  );

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
          <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
            新規登録
          </Typography>

          {localError && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{localError}</Alert>
          )}
          {successMsg && (
            <Alert severity="success" sx={{ width: '100%', mb: 2 }}>{successMsg}</Alert>
          )}

          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}

          <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
            <Grid item>
              <Link to="/login" style={{ textDecoration: 'none', color: '#1976d2' }}>
                すでにアカウントをお持ちの方はこちら (ログイン)
              </Link>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Container>
  );
};

export default Register;
